import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Animated, StyleSheet, TouchableOpacity, View, Dimensions } from 'react-native';

import { Color, CommonProperties } from '../../styles';
import { scale, scaleVertically } from '../../utils/scale.utils';
import BaseText from '../BaseText';
import { selectExpenseStates } from '../../store/selectors/expense.selector';
import { EXPENSE_ACTION_TYPES } from '../../constants/Enums';
import { REPORT_TOGGLE_OPTIONS, REPORT_TOGGLE_KEY } from '../../constants/ExpenseCategories';
import { setExpenseValue } from '../../store/actions/expense';
import { isNullOrEmpty } from '../../utils/common.utils';

const ReportsToggle = ({ subTab, isDisabled = false }) => {
  const expenseStates = useSelector(selectExpenseStates)[EXPENSE_ACTION_TYPES.CREATION][subTab];
  const dispatch = useDispatch();
  const selectedOption = expenseStates[REPORT_TOGGLE_KEY];
  const animation = useRef(
    new Animated.Value(
      isNullOrEmpty(selectedOption) || selectedOption === REPORT_TOGGLE_OPTIONS.NEW_REPORT.label
        ? 0
        : 1
    )
  ).current;

  const handleToggle = (option) => {
    if (option !== selectedOption) {
      dispatch(
        setExpenseValue({
          actionType: EXPENSE_ACTION_TYPES.CREATION,
          subTab,
          key: REPORT_TOGGLE_KEY,
          value: option,
        })
      );
      // Trigger animation (0 for left, 1 for right)
      Animated.timing(animation, {
        toValue: option === REPORT_TOGGLE_OPTIONS.NEW_REPORT.label ? 0 : 1,
        duration: 300, // Adjust duration for effect speed
        useNativeDriver: false,
      }).start();
    }
  };

  // Interpolate animation for the pill movement
  const pillPosition = animation.interpolate({
    inputRange: [0, 1],
    outputRange: ['0%', '50%'], // Moves the pill left or right
  });

  return (
    <View style={styles.wrapper}>
      <View
        style={[styles.container, isDisabled ? styles.disabledContainer : styles.enabledContainer]}
      >
        {/* Animated pill background */}
        <Animated.View
          style={[
            styles.activeBackground,
            selectedOption === REPORT_TOGGLE_OPTIONS.NEW_REPORT.label
              ? styles.newReportActiveRadius
              : styles.existingActiveRadius,
            {
              left: pillPosition, // Adjust position dynamically
            },
          ]}
        />
        <TouchableOpacity
          onPress={() => handleToggle(REPORT_TOGGLE_OPTIONS.NEW_REPORT.label)}
          style={styles.button}
          disabled={isDisabled}
        >
          <BaseText
            style={[
              styles.text,
              isNullOrEmpty(selectedOption) ||
              selectedOption === REPORT_TOGGLE_OPTIONS.NEW_REPORT.label
                ? styles.activeText
                : styles.inactiveText,
            ]}
            // numberOfLines={1}
            // ellipsizeMode="tail"
          >
            {REPORT_TOGGLE_OPTIONS.NEW_REPORT.label}
          </BaseText>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => handleToggle(REPORT_TOGGLE_OPTIONS.EXISTING.label)}
          style={styles.button}
          disabled={isDisabled}
        >
          <BaseText
            style={[
              styles.text,
              isDisabled
                ? styles.disabledText
                : selectedOption === REPORT_TOGGLE_OPTIONS.EXISTING.label
                ? styles.activeText
                : styles.inactiveText,
            ]}
          >
            {REPORT_TOGGLE_OPTIONS.EXISTING.label}
          </BaseText>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const { width: screenWidth } = Dimensions.get('window');
const borderRadius = scale(16);
const activeBorderRadius = borderRadius - scale(1);

const styles = StyleSheet.create({
  wrapper: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    width: screenWidth * 0.58,
    flexDirection: 'row',
    borderRadius,
    overflow: 'hidden',
    position: 'relative',
    height: scaleVertically(36),
  },
  disabledContainer: {
    backgroundColor: Color.lightgray,
  },
  enabledContainer: {
    borderWidth: 1,
    borderColor: Color.active,
  },
  button: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingLeft: scale(2),
    paddingRight: scale(2),
  },
  activeBackground: {
    flex: 1,
    position: 'absolute',
    width: '50%', // 52% of container width
    height: '100%',
    backgroundColor: Color.active,
    zIndex: -1, // Place behind buttons
  },
  newReportActiveRadius: {
    borderTopRightRadius: activeBorderRadius,
    borderBottomRightRadius: activeBorderRadius,
    ...CommonProperties.shadow,
  },
  existingActiveRadius: {
    borderTopLeftRadius: activeBorderRadius,
    borderBottomLeftRadius: activeBorderRadius,
    ...CommonProperties.leftShadow,
  },
  text: {
    color: Color.active,
    ...CommonProperties.custom,
  },
  activeText: {
    color: Color.accent,
  },
  inactiveText: {
    color: Color.primary,
  },
  disabledText: {
    color: Color.disabledText,
  },
});

export default ReportsToggle;
